<template>
  <div>
    <h3 class="text-size-lg-md weight--heavier layout--margin-bottom-md">
      1. Select a New Plan
    </h3>
    <div class="button__toggle-wrap layout--padding-bottom-lg">
      <div class="layout--block">
        <input
          type="radio"
          id="monthly"
          name="payment-plan"
          value="monthly"
          v-model="paymentPlan"
          class="layout--hide"
        />
        <label
          for="monthly"
          class="button button--toggle button--toggle-left layout--text-left"
          :class="{ 'button--toggle-active': paymentPlan === 'monthly' }"
        >
          <span
            class="input-group__radio-icon"
            :class="{
              'input-group__radio-icon--active': paymentPlan !== 'annual',
            }"
          />
          Bill Monthly
        </label>
      </div>
      <div
        class="layout--flex-column layout--flex-align-start layout--text-center"
      >
        <input
          type="radio"
          id="annual"
          name="payment-plan"
          value="annual"
          v-model="paymentPlan"
          class="layout--hide"
        />
        <label
          for="annual"
          class="button button--toggle button--toggle-right layout--text-left"
          :class="{ 'button--toggle-active': paymentPlan === 'annual' }"
        >
          <span
            class="input-group__radio-icon"
            :class="{
              'input-group__radio-icon--active': paymentPlan === 'annual',
            }"
          />
          Bill Annually <i>(Save 20%)</i></label
        >
      </div>
    </div>
    <ul class="account__plan-choice-list layout--margin-bottom-lg">
      <li
        v-for="option in sortedSubscriptionOptions"
        :key="option.subscriptionType"
        class="account__plan-choice-list-item"
        :class="{
          'account__plan-choice-list-item--inactive':
            option.subscriptionType === subscription.subscriptionType,
        }"
      >
        <h4 class="text-size-sm layout--margin-bottom-xs">
          {{ option.label }}
        </h4>
        <p class="text-size-xl weight--bold layout--margin-bottom-xxs">
          {{ option.monthlyCredits | abbreviateMillion }}
        </p>
        <p>
          <IconEyeGreen
            class="icon icon--medium layout--margin-right-xxs"
          />Monthly Views
        </p>
        <Divider dark class="layout--margin-top-md layout--margin-bottom-md" />
        <p class="text-size-md">
          {{ getPriceLabel(option) }}
        </p>
        <Divider dark class="layout--margin-top-md layout--margin-bottom-md" />
        <ul class="account__plan-feature-list">
          <li
            class="account__plan-feature-list-item account__plan-feature-list-item--active"
          >
            Unlimited Timers
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active': !subscriptionOption(
                option.subscriptionType
              ).hasBranding,
            }"
          >
            Branding removed
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active': subscriptionOption(
                option.subscriptionType
              ).hasDynamicLocal,
            }"
          >
            Dynamic Language
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active':
                subscriptionOption(option.subscriptionType).costPerCredit > 0,
            }"
          >
            Additional views<br />
            <i>
              {{
                subscriptionOption(option.subscriptionType).costPerCredit > 0
                  ? "($" +
                    subscriptionOption(option.subscriptionType).costPerCredit +
                    "/view)"
                  : "None"
              }}</i
            >
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active': subscriptionOption(
                option.subscriptionType
              ).hasRollover,
            }"
          >
            Credits rollover
          </li>
        </ul>
        <p
          v-if="option.subscriptionType === subscription.subscriptionType"
          class="account__plan-current"
        >
          Your Current Plan
        </p>
        <router-link
          v-if="
            typeof subscription.subscriptionType !== 'undefined' &&
              subscription.subscriptionType !== option.subscriptionType &&
              option.subscriptionType !== 0
          "
          :to="{
            name: 'choose billing period',
            params: { subscriptionType: option.subscriptionType },
          }"
          type="button"
          class="button button--fixed-width button--no-x-padding layout--block layout--margin-top-lg"
          >Select {{ option.label }}</router-link
        >
        <router-link
          v-if="
            typeof subscription.subscriptionType !== 'undefined' &&
              subscription.subscriptionType !== option.subscriptionType &&
              option.subscriptionType === 0 &&
              !(isEarlyBird || isVip)
          "
          :to="{
            name: 'summary',
            params: { subscriptionType: 0, billingPeriod: 0 },
          }"
          type="button"
          class="button button--fixed-width button--no-x-padding layout--block layout--margin-top-lg"
          >Select {{ option.label }}</router-link
        >
        <button
          v-if="
            typeof subscription.subscriptionType !== 'undefined' &&
            subscription.subscriptionType !== option.subscriptionType &&
            option.subscriptionType === 0 &&
            (isEarlyBird || isVip)
          "
          disabled 
          type="button" 
          class="button button--fixed-width button--no-x-padding layout--block layout--margin-top-lg"
          >Not Available</button
        >
      </li>
    </ul>
    <p class="weight--light">
      Prices are charged on a recurring monthly or annual basis. Unused credits
      rollover into the following month for specific plans.<br />* Per
      impression overage rates apply to all timers rendered over monthly plan
      credits. Minimum overage fee is $1.<br />
      ** Rollover credits cannot be transfered between plans.
    </p>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import Divider from "../../../components/Divider";

import IconEyeGreen from "../../../assets/icons/iconImpressionsEye-Green.svg";

export default {
  name: "SelectPlan",
  components: { IconEyeGreen, Divider },
  mounted: function() {
    if (this.sortedSubscriptionOptions.length === 0) {
      this.getSubscriptionOptions();
    }
  },
  data() {
    return {
      paymentPlan: "monthly",
    };
  },
  methods: {
    ...mapActions("account", ["getSubscriptionOptions"]),
    /**
     * Returns the correct monthly / annual price with label at the end unless
     * it's a free account, then there's no label at the end
     */
    getPriceLabel: function(option) {
      let priceLabel = "";
      let paymentPlanLabel = "";
      if (this.paymentPlan === "annual") {
        priceLabel = option.annualPriceLabel;
        paymentPlanLabel = "/year";
      } else {
        priceLabel = option.monthlyPriceLabel;
        paymentPlanLabel = "/month";
      }
      if (option.subscriptionType !== 0) {
        return priceLabel + paymentPlanLabel;
      }
      return priceLabel;
    },
  },
  computed: {
    ...mapGetters("account", [
      "subscriptionOption",
      "sortedSubscriptionOptions",
      "subscription",
      "averageImpressions",
    ]),
    isEarlyBird() {
      return this.subscription.subscriptionType === 1;
    },
    isVip() {
      return this.subscription.subscriptionType === 2;
    },
  },
};
</script>

<style></style>
